import { createContext, use, useState } from 'react'
import { apiFetch } from '@/lib/api'

const SessionContext = createContext(null)

export function SessionProvider({ initial, children }) {
  const [currentUser, setCurrentUser] = useState(initial?.currentUser ?? null)
  const [csrfToken, setCsrfToken] = useState(initial?.csrfToken ?? null)
  const [stepOneFlowVersion] = useState(initial?.stepOneFlowVersion ?? 'guarded_v4')

  const postJson = (path, body, method = 'POST') =>
    apiFetch(path, { method, csrfToken, body })

  const signIn = async ({ email, password }) => {
    const res = await postJson('/users/sign_in', { user: { email, password } })
    if (res.two_factor_required) return { twoFactorRequired: true }

    setCurrentUser(res.user)
    setCsrfToken(res.csrf_token)
    return res.user
  }

  const signUp = (values) => postJson('/users', { user: values })

  const signOut = async () => {
    const res = await postJson('/users/sign_out', undefined, 'DELETE')
    setCurrentUser(null)
    setCsrfToken(res.csrf_token)
  }

  const requestPasswordReset = ({ email }) =>
    postJson('/users/password', { user: { email } })

  const resetPassword = ({ token, password, password_confirmation }) =>
    postJson(
      '/users/password',
      { user: { reset_password_token: token, password, password_confirmation } },
      'PUT',
    )

  const refresh = async () => {
    try {
      setCurrentUser(await apiFetch('/users/me'))
    } catch {
      setCurrentUser(null)
    }
  }

  const updateProfile = async ({ first_name, last_name, email }) => {
    const user = await postJson(
      '/users/profile',
      { user: { first_name, last_name, email } },
      'PATCH',
    )
    setCurrentUser(user)
    return user
  }

  const updatePassword = async ({ current_password, password, password_confirmation }) => {
    const user = await postJson(
      '/users/password_change',
      { user: { current_password, password, password_confirmation } },
      'PATCH',
    )
    setCurrentUser(user)
    return user
  }

  const updateSessionTimeout = async ({ session_timeout_hours }) => {
    const user = await postJson(
      '/users/session_timeout',
      { user: { session_timeout_hours } },
      'PATCH',
    )
    setCurrentUser(user)
    return user
  }

  const verifyTwoFactor = async ({ code }) => {
    const res = await postJson('/users/two_factor', { code })
    setCurrentUser(res.user)
    setCsrfToken(res.csrf_token)
    return res.user
  }

  const verifyTwoFactorBackup = async ({ code }) => {
    const res = await postJson('/users/two_factor/backup', { code })
    setCurrentUser(res.user)
    setCsrfToken(res.csrf_token)
    return res.user
  }

  const beginTwoFactorSetup = () =>
    apiFetch('/api/v1/two_factor/setup', { csrfToken })

  const confirmTwoFactor = async ({ code }) => {
    const res = await postJson('/api/v1/two_factor/confirm', { code })
    setCurrentUser(res.user)
    return res.user
  }

  const disableTwoFactor = async ({ code, backup_code }) => {
    const res = await postJson('/api/v1/two_factor/disable', { code, backup_code })
    setCurrentUser(res.user)
    return res.user
  }

  const regenerateBackupCodes = async ({ code, backup_code }) =>
    postJson('/api/v1/two_factor/regenerate_backup_codes', { code, backup_code })

  return (
    <SessionContext
      value={{
        currentUser,
        csrfToken,
        stepOneFlowVersion,
        signIn,
        signUp,
        signOut,
        requestPasswordReset,
        resetPassword,
        refresh,
        updateProfile,
        updatePassword,
        updateSessionTimeout,
        verifyTwoFactor,
        verifyTwoFactorBackup,
        beginTwoFactorSetup,
        confirmTwoFactor,
        disableTwoFactor,
        regenerateBackupCodes,
      }}
    >
      {children}
    </SessionContext>
  )
}

export function useSession() {
  const ctx = use(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside SessionProvider')
  return ctx
}
